import type { AppTransition } from './appInvocationLifecycle';

/** How many events one bounded log keeps. Old history is dropped, never replayed. */
export const APP_DIAGNOSTIC_LIMIT = 80;

export interface BoundedLogSnapshot<T> {
  /** Bumped once per recorded event, so a reader can skip unchanged snapshots. */
  revision: number;
  /** Oldest first, bounded to the limit. */
  events: readonly T[];
  /** Evicted from the front of the buffer. */
  dropped: number;
}

export interface BoundedLog<T> {
  record(event: T): void;
  subscribe(listener: () => void): () => void;
  /** The same object until an event is recorded. */
  getSnapshot(): BoundedLogSnapshot<T>;
}

/**
 * A bounded, immutable event log. Recording never touches the state store, so
 * a rejected write can be observed even when no domain state changed.
 * Listeners run against a copy: a subscriber may record or unsubscribe
 * reentrantly without reordering or skipping notifications.
 */
export function createBoundedLog<T>(
  limit = APP_DIAGNOSTIC_LIMIT,
): BoundedLog<T> {
  const size = Math.max(1, Math.floor(limit));
  let events: readonly T[] = [];
  let dropped = 0;
  let revision = 0;
  let snapshot: BoundedLogSnapshot<T> = { revision, events, dropped };
  const listeners = new Set<() => void>();

  return {
    record(event) {
      const overflow = events.length + 1 - size;
      events =
        overflow > 0 ? [...events.slice(overflow), event] : [...events, event];
      dropped += Math.max(0, overflow);
      revision += 1;
      snapshot = { revision, events, dropped };
      for (const listener of [...listeners]) {
        try {
          listener();
        } catch {
          // A broken observer must not break the write that notified it.
        }
      }
    },
    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    getSnapshot() {
      return snapshot;
    },
  };
}

export type AppDiagnostics = BoundedLog<AppTransition>;

/** The App controller's own diagnostic log. */
export function createAppDiagnostics(
  limit = APP_DIAGNOSTIC_LIMIT,
): AppDiagnostics {
  return createBoundedLog<AppTransition>(limit);
}
